import { createClient, type User, type Session } from '@supabase/supabase-js';
import { useAuthStore } from '@/stores/authStore';
// NOTE: Do NOT import stores or services that import stores here
// It creates circular dependencies via: learningStore -> useLanguageStore -> supabase -> learningStore
import { logSecurityEvent } from '@/config/security';
import { validateInput } from '@/utils/security-headers';
import type { Database } from '@/types/database.types';
import { authService } from './auth';
// Dynamic imports to avoid circular dependency - these services import useLearningStore
const getRealtimeService = () => import('./realtime').then(m => m.realtimeService);
const getProgressService = () => import('./progress').then(m => m.progressService);

// Environment variables - these should be set in .env file
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase URL and/or Anonymous Key not found in environment variables');
}

// Create Supabase client with robust authentication
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce' // Use PKCE flow for enhanced security
  },
  realtime: {
    params: {
      eventsPerSecond: 10 // Rate limiting for real-time events
    }
  },
  global: {
    headers: {
      'X-Client-Info': 'converlang-web/1.0.0',
      'Content-Type': 'application/json; charset=utf-8'  // Explicit UTF-8 encoding for Spanish accents
    }
  }
});

// Auth debugging utility
export const debugAuthState = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  return { session, error };
};

// Authentication service

// Real-time sync service

// Spanish Vocabulary Service

// Progress sync service

// Helper function to clear cached auth data (useful for debugging)
export const clearAuthCache = () => {
  localStorage.removeItem('converlang-auth');
  localStorage.removeItem('supabase.auth.token');
  sessionStorage.clear();
};

// Path that the password-recovery email link redirects to (see auth.service.ts
// resetPassword `redirectTo`). A session that lands here must be treated as a
// recovery session — never promoted to a full app sign-in.
const PASSWORD_RECOVERY_PATH = '/auth/update-password';

// Authentication state stabilization promise
let authInitializationPromise: Promise<boolean> | null = null;
let isAuthInitialized = false;
let currentUserId: string | null = null;
let isPasswordRecoverySession = false;

/**
 * True while a password-recovery session is active (the user clicked a reset
 * link but has not yet set a new password). Consumed by the update-password
 * page guard so the form is only shown when a valid recovery token exists.
 */
export const isInPasswordRecovery = (): boolean => isPasswordRecoverySession;

// Whether the current browser location is the password-recovery landing page.
const isOnRecoveryPath = (): boolean =>
  typeof window !== 'undefined' && window.location.pathname === PASSWORD_RECOVERY_PATH;

// Initialize auth state listener with proper race condition handling
export const initializeAuthListener = () => {
  if (authInitializationPromise) {
    return authInitializationPromise;
  }

  authInitializationPromise = (async () => {
    try {
      const { setLoading } = useAuthStore.getState();
      setLoading(true);

      // STEP 1: Get initial session synchronously
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        console.error('Error getting initial session:', error);
        setLoading(false);
        return false;
      }

      // STEP 2: Process initial session if it exists
      if (session?.user) {
        // A recovery link lands the user on PASSWORD_RECOVERY_PATH. Detecting
        // session-in-URL may fire PASSWORD_RECOVERY before this listener is
        // attached, so guard here by path: a recovery session must NOT be
        // promoted to a full sign-in (that would be a silent login).
        if (isOnRecoveryPath()) {
          isPasswordRecoverySession = true;
          setLoading(false);
        } else {
          currentUserId = session.user.id;
          await processAuthSession(session.user, session);
        }
      } else {
        setLoading(false);
      }

      // STEP 3: Set up the listener AFTER initial session is processed
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
        // Password recovery: route to the dedicated set-new-password page and do
        // NOT process the recovery session as a normal sign-in.
        if (event === 'PASSWORD_RECOVERY') {
          isPasswordRecoverySession = true;
          if (typeof window !== 'undefined' && !isOnRecoveryPath()) {
            window.location.assign(PASSWORD_RECOVERY_PATH);
          }
          return;
        }

        // Prevent processing during initialization to avoid race conditions
        if (!isAuthInitialized && event === 'SIGNED_IN') {
          return;
        }

        // Skip token refresh events - handled internally by Supabase SDK
        if (event === 'TOKEN_REFRESHED') {
          return;
        }

        if (event === 'SIGNED_IN' && session?.user) {
          // A sign-in originating from the recovery landing page is a recovery
          // session, not a real login — keep it isolated to the reset flow.
          if (isOnRecoveryPath()) {
            isPasswordRecoverySession = true;
            return;
          }
          // Skip if same user (tab-focus session recovery, not a real sign-in)
          if (session.user.id === currentUserId) {
            return;
          }
          currentUserId = session.user.id;
          await processAuthSession(session.user, session);
        } else if (event === 'SIGNED_OUT') {
          currentUserId = null;
          isPasswordRecoverySession = false;
          await handleSignOut();
        }
      });

      // Mark auth as initialized to allow state changes
      isAuthInitialized = true;
      return true;

    } catch (error) {
      console.error('Error initializing auth listener:', error);
      useAuthStore.getState().setLoading(false);
      return false;
    }
  })();

  return authInitializationPromise;
};

// Centralized authentication session processing
async function processAuthSession(user: User, session: Session) {
  const { setLoading, setSession } = useAuthStore.getState();

  try {
    setLoading(true);

    // Clear previous user's subscription data before processing new session
    try {
      const { useSubscriptionStore } = await import('@/stores/subscriptionStore');
      useSubscriptionStore.getState().clearSubscription();
    } catch (e) {
      console.warn('Failed to clear subscription store (non-blocking):', e);
    }

    // Clear previous user's XP and daily-progress state to prevent cross-user
    // contamination (e.g. seeing User A's xpToday after User B logs in)
    try {
      const { useXpStore } = await import('@/stores/xpStore');
      useXpStore.getState().reset();
    } catch (e) {
      console.warn('Failed to clear XP store (non-blocking):', e);
    }
    try {
      const { dailyProgressService } = await import('./daily-progress');
      dailyProgressService.clearAllCaches();
    } catch (e) {
      console.warn('Failed to clear daily-progress cache (non-blocking):', e);
    }

    // CRITICAL: Set session and user data immediately and synchronously
    setSession(session);
    await authService.syncUserToStore(user, session);

    // Mark loading as complete before background services
    setLoading(false);

    // Background services - non-blocking (using dynamic imports to avoid circular deps)
    Promise.all([
      getRealtimeService().then(svc => svc.initializeRealtime()).catch(error => {
        console.warn('Real-time initialization failed:', error);
      }),
      getProgressService().then(svc => svc.fetchUserProgress()).catch(error => {
        console.warn('Progress sync failed:', error);
      })
    ]).catch(error => {
      console.warn('Background services initialization failed:', error);
    });

  } catch (error) {
    console.error('Error processing auth session:', error);
    setLoading(false);
    throw error; // Propagate error for proper handling
  }
}

// Centralized sign-out handling
async function handleSignOut() {
  const { clearAuth } = useAuthStore.getState();

  try {
    // Clear subscription state before auth (while userId might still be available)
    const { useSubscriptionStore } = await import('@/stores/subscriptionStore');
    useSubscriptionStore.getState().clearSubscription();

    // Clear XP and daily-progress state so the next login starts clean
    try {
      const { useXpStore } = await import('@/stores/xpStore');
      useXpStore.getState().reset();
    } catch (e) {
      console.warn('Failed to clear XP store on sign-out (non-blocking):', e);
    }
    try {
      const { dailyProgressService } = await import('./daily-progress');
      dailyProgressService.clearAllCaches();
    } catch (e) {
      console.warn('Failed to clear daily-progress cache on sign-out (non-blocking):', e);
    }

    // Then clean up general realtime (using dynamic import to avoid circular deps)
    const realtimeService = await getRealtimeService();
    realtimeService.cleanup();

    // Finally clear auth
    clearAuth();

  } catch (error) {
    console.error('Error handling sign out:', error);
    // Still clear auth even if cleanup fails
    clearAuth();
  }
}

// Export function to check if auth is ready for data operations
export const isAuthReady = (): boolean => {
  const { user, isLoading } = useAuthStore.getState();
  return isAuthInitialized && !!user?.id && !isLoading;
};

// Legacy function - now handled by processAuthSession
// Kept for backward compatibility but deprecated
const handleUserSignIn = async (user: User, session: Session) => {
  await processAuthSession(user, session);
};

export default supabase;
